import { Button, styled, useTheme } from '@mui/material';
import { Typography } from '../../../../components/Typography/Typography';

const ACTIVATE_OUTLINE = '#439C3D';
const DEACTIVATE_OUTLINE = '#FF8A94';

type Props = {
  isActive: boolean;
  disabled?: boolean;
  onDeactivate: () => void;
  onActivate: () => void;
};

/**
 * Standalone account-state section shown at the bottom of the User modal (moved out of the header).
 * Design + placement only; it calls the same deactivate/activate handlers as before.
 */
export const DeactivateSection = ({ isActive, disabled, onDeactivate, onActivate }: Props) => {
  const { colors, palette } = useTheme();
  const accent = isActive ? DEACTIVATE_OUTLINE : ACTIVATE_OUTLINE;
  const activateLabelColor = palette.mode === 'dark' ? colors.text.main : ACTIVATE_OUTLINE;

  return (
    <Section $accent={accent}>
      <TextWrap>
        <Typography variant="subtitle2" color={colors.text.main}>
          {isActive ? 'Deactivate user' : 'Reactivate user'}
        </Typography>
        <Typography variant="caption" color={colors.text.secondary}>
          {isActive
            ? 'They lose access right away. Their history stays, and you can reactivate them later.'
            : 'They regain access right away, with their history intact.'}
        </Typography>
      </TextWrap>
      <Button
        variant="outlined"
        onClick={isActive ? onDeactivate : onActivate}
        disabled={disabled}
        sx={{
          flexShrink: 0,
          textTransform: 'none',
          height: 36,
          padding: '0 16px',
          fontSize: '13px',
          fontWeight: 500,
          '&.MuiButton-outlined': {
            borderColor: accent,
            color: isActive ? DEACTIVATE_OUTLINE : activateLabelColor,
            backgroundColor: 'transparent',
          },
          '&.MuiButton-outlined:hover': {
            borderColor: accent,
            color: isActive ? DEACTIVATE_OUTLINE : activateLabelColor,
            backgroundColor: isActive ? 'rgba(255, 138, 148, 0.12)' : 'rgba(67, 156, 61, 0.14)',
          },
          '&.MuiButton-outlined.Mui-disabled': {
            borderColor: colors.border.default,
            color: colors.text.caption,
          },
        }}
      >
        {isActive ? 'Deactivate' : 'Activate'}
      </Button>
    </Section>
  );
};

const Section = styled('div')<{ $accent: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${({ $accent }) => $accent};
  background-color: ${({ $accent }) => `${$accent}14`};
`;

const TextWrap = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;
